// The site's languages. English pages have no prefix (/videos); the others live under their code
// (/de/videos, /ru/categories/milf). Inside the app every page sits under app/[lang]: src/proxy.ts
// rewrites an English address to /en/… so the same routes serve all of them. Plain TypeScript: the
// proxy, client and server code all use it.

export const LOCALES = ["en", "de", "es", "fr", "it", "pt", "ru", "ja", "pl", "nl"] as const;
export type Locale = (typeof LOCALES)[number];
export const DEFAULT_LOCALE: Locale = "en";

export const isLocale = (v: unknown): v is Locale => typeof v === "string" && (LOCALES as readonly string[]).includes(v);

/** Each language in its own words, for the language menu. */
export const LOCALE_NAMES: Record<Locale, string> = {
  en: "English",
  de: "Deutsch",
  es: "Español",
  fr: "Français",
  it: "Italiano",
  pt: "Português",
  ru: "Русский",
  ja: "日本語",
  pl: "Polski",
  nl: "Nederlands",
};

/** Addresses that are the same in every language (not pages of the site's own). */
const SHARED = /^\/(api|admin|thumbs|media|go|r|embed|_next)(\/|\?|$)/;

/** A site path in a language: "/videos" stays as it is in English and becomes "/de/videos" in German. */
export function localePath(lang: string | null | undefined, path: string): string {
  if (!path.startsWith("/") || path.startsWith("//") || SHARED.test(path) || !lang || lang === DEFAULT_LOCALE || !isLocale(lang)) return path;
  return path === "/" ? `/${lang}` : path.startsWith("/?") ? `/${lang}${path.slice(1)}` : `/${lang}${path}`;
}

/**
 * A site path in a language unless it names one already ("/de/videos" stays as it is): for addresses passed
 * along, such as a form's `next=` after logging in.
 */
export function inLocale(lang: string | null | undefined, path: string): string {
  return splitLocale(path.split(/[?#]/)[0]).lang !== DEFAULT_LOCALE ? path : localePath(lang, path);
}

/** A browser path's language and the path without it: "/de/videos" → de, "/videos". */
export function splitLocale(pathname: string): { lang: Locale; path: string } {
  const first = pathname.split("/")[1];
  if (isLocale(first) && first !== DEFAULT_LOCALE) return { lang: first, path: pathname.slice(first.length + 1) || "/" };
  return { lang: DEFAULT_LOCALE, path: pathname };
}

/** hreflang alternates of a page (Next.js metadata `alternates.languages`): every language and x-default. */
export function languageAlternates(path: string): Record<string, string> {
  return Object.fromEntries([...LOCALES.map((l) => [l, localePath(l, path)]), ["x-default", path]]);
}
